import React, { useEffect, useMemo, useState } from 'react';
import { Activity, CalendarRange, TrendingUp } from 'lucide-react';
import { PageEnvironmentSelector } from '../components/PageEnvironmentSelector';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { activeUsersService, ActiveUsersRange } from '../services/activeUsers.service';
import { resolveObjectIds } from '../services/graphService';

type ActiveUsersPoint = {
  bucket: string;
  users: number;
};

const rangeOptions: Array<{ key: ActiveUsersRange; label: string; description: string; accent: string; icon: typeof Activity }> = [
  {
    key: '24h',
    label: '24 Hours',
    description: 'Daily active users',
    accent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    icon: Activity,
  },
  {
    key: '7d',
    label: '7 Days',
    description: 'Average daily active users (7 days)',
    accent: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    icon: CalendarRange,
  },
  {
    key: '30d',
    label: '30 Days',
    description: 'Average daily active users (30 days)',
    accent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    icon: TrendingUp,
  },
];

export const ActiveUsersPage: React.FC = () => {
  const { currentEnvironment } = useEnvironment();
  const [range, setRange] = useState<ActiveUsersRange>('7d');
  const [activeUsers, setActiveUsers] = useState<number | null>(null);
  const [trend, setTrend] = useState<ActiveUsersPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeUserIds, setActiveUserIds] = useState<string[]>([]);
  const [userDisplayNames, setUserDisplayNames] = useState<Record<string, string>>({});
  const [userSearch, setUserSearch] = useState('');

  const filteredUserIds = activeUserIds.filter((id) =>
    id.toLowerCase().includes(userSearch.toLowerCase()) ||
    userDisplayNames[id]?.toLowerCase().includes(userSearch.toLowerCase())
  );

  useEffect(() => {
  
  const fetchActiveUsers = async () => {
    try {

      setLoading(true);
      setError(null);

      const response =
        await activeUsersService.getActiveUsers(range);

      setActiveUsers(response.activeUsers);
      setTrend(response.trend || []);
      setActiveUserIds(response.activeUserIds || []);
    } catch (error) {
      console.error(
        'Failed to fetch active users:',
        error
      );

      setActiveUsers(null);
      setTrend([]);
      setActiveUserIds([]);

      setError(
        error instanceof Error
          ? error.message
          : 'Unable to load active users.'
      );
    } finally {
      setLoading(false);
    }
  };

  fetchActiveUsers();
}, [range, currentEnvironment]);

  useEffect(() => {
    let cancelled = false;
    setUserDisplayNames({});

    if (activeUserIds.length === 0) {
      return () => {
        cancelled = true;
      };
    }

    resolveObjectIds(activeUserIds)
      .then((resolvedUsers) => {
        if (cancelled) return;

        const displayNames: Record<string, string> = {};
        for (const [objectId, user] of Object.entries(resolvedUsers)) {
          if (!user.error && user.displayName && user.displayName !== objectId) {
            displayNames[objectId] = user.displayName;
          }
        }
        setUserDisplayNames(displayNames);
      })
      .catch((resolveError) => {
        console.error('Failed to resolve active user names:', resolveError);
      });

    return () => {
      cancelled = true;
    };
  }, [activeUserIds]);

  const selectedOption = rangeOptions.find((option) => option.key === range) ?? rangeOptions[1];
  const periodDateRange = useMemo(() => {
    const dayCount = range === '24h' ? 1 : range === '7d' ? 7 : 30;
    const endDate = new Date();
    endDate.setUTCHours(0, 0, 0, 0);
    endDate.setUTCDate(endDate.getUTCDate() - 1);
    const startDate = new Date(endDate);
    startDate.setUTCDate(startDate.getUTCDate() - dayCount + 1);

    const dateFormat: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    };

    if (range === '24h') {
      return endDate.toLocaleDateString('en-GB', dateFormat);
    }

    return `${startDate.toLocaleDateString(undefined, dateFormat)} – ${endDate.toLocaleDateString(undefined, dateFormat)}`;
  }, [range]);
  const maxTrendValue = useMemo(
    () => Math.max(1, ...trend.map((point) => point.users)),
    [trend]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Active Users</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Unique users with audit activity during the selected period.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <PageEnvironmentSelector />
          <div className="inline-flex rounded-lg border border-gray-300 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-800">
            {rangeOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setRange(option.key)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  range === option.key
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{selectedOption.description}</p>
            <div className="mt-3 text-4xl font-bold text-gray-900 dark:text-white">
              {loading
                ? '...'
                : activeUsers == null
                  ? 0
                  : Number.isInteger(activeUsers)
                    ? activeUsers
                    : activeUsers.toFixed(1)}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {periodDateRange}
            </span>
            <div className={`rounded-full p-3 ${selectedOption.accent}`}>
              <selectedOption.icon className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

<div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
        Active User Details
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Display name and object ID for users active during the selected period
      </p>
    </div>

    <span className="text-sm text-gray-500 dark:text-gray-400">
      {activeUserIds.length} users returned
    </span>
  </div>

  <input
    type="text"
    value={userSearch}
    onChange={(e) => setUserSearch(e.target.value)}
    placeholder="Search name or object ID..."
    className="mb-4 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
  />

  {loading ? (
    <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
      Loading active users...
    </p>
  ) : filteredUserIds.length === 0 ? (
    <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
      No matching user IDs found.
    </p>
  ) : (
    <div className="max-h-80 overflow-y-auto">
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {filteredUserIds.map((userId) => (
          <li
            key={userId}
            className="flex items-center justify-between gap-3 py-3"
          >
            <div className="min-w-0">
              {userDisplayNames[userId] && (
                <span className="block text-sm font-medium text-gray-900 dark:text-white">
                  {userDisplayNames[userId]}
                </span>
              )}
              <span className="block break-all text-sm text-gray-500 dark:text-gray-400">
                {userId}
              </span>
            </div>

            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(userId);
              }}
              title="Copy user ID"
              aria-label={`Copy ${userId}`}
              className="shrink-0 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
            >
            </button>
          </li>
        ))}
      </ul>
    </div>
  )}
</div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Activity trend</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Unique users by day for {selectedOption.label.toLowerCase()}</p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="h-3 w-full animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            ))}
          </div>
        ) : trend.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
            No audit activity was found for this time range.
          </div>
        ) : (
          <div className="space-y-3">
            {trend.map((point) => {
              const width = `${Math.max(8, (point.users / maxTrendValue) * 100)}%`;
              return (
                <div key={point.bucket} className="grid grid-cols-[80px_1fr_52px] items-center gap-3 text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    {new Date(point.bucket).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                  </span>
                  <div className="h-2.5 rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-500"
                      style={{ width }}
                    />
                  </div>
                  <span className="text-right font-medium text-gray-700 dark:text-gray-200">{point.users}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveUsersPage;